import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart } from "@fortawesome/free-solid-svg-icons";
import CardSVG from "../../assets/cart-plus-svgrepo-com.svg";
import { isFavorite, calculateDiscountedPrice } from "../../utils/localStorage";
import { language } from "../../language/Language";

/**
 * ProductCard — كارت منتج مشترك بين كل صفحات عرض المنتجات.
 *
 * Props:
 *  - product: object { product_id | id, name, price, default_sales_discount, image_url, stock }
 *  - onAddToCart(product, qty, event)
 *  - onAddToFavourite(product, event)
 */
const ProductCard = React.memo(({ product, onAddToCart, onAddToFavourite }) => {
    const [qty, setQty] = useState(1);

    // normalize id — بعض الصفحات بتبعت product_id وبعضها id
    const productId = product?.product_id ?? product?.id;

    const discountedPrice = useMemo(
        () => calculateDiscountedPrice(product?.price, product?.default_sales_discount),
        [product?.price, product?.default_sales_discount]
    );
    const originalPrice = useMemo(
        () => parseInt(product?.price).toFixed(),
        [product?.price]
    );
    const finalPrice = useMemo(
        () => parseFloat(discountedPrice).toFixed(0),
        [discountedPrice]
    );
    const hasDiscount = originalPrice !== finalPrice;
    const maxQty = Math.min(parseFloat(product?.stock) || 99, 99);

    const decrease = (e) => {
        e.stopPropagation();
        setQty((prev) => Math.max(1, prev - 1));
    };

    const increase = (e) => {
        e.stopPropagation();
        setQty((prev) => Math.min(maxQty, prev + 1));
    };

    return (
        <div className="bg-white rounded-2xl p-5 cursor-pointer hover:-translate-y-2 transition-all relative flex flex-col">
            {/* discount badge */}
            {product?.percent && (
                <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {product.percent}
                </div>
            )}

            {/* favourite button */}
            <div className="bg-gray-100 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer absolute top-4 right-4">
                <FontAwesomeIcon
                    className={`${
                        isFavorite(productId)
                            ? "text-HovMainColor"
                            : "text-slate-500 hover:text-green-400"
                    } transition-colors`}
                    onClick={(e) => onAddToFavourite && onAddToFavourite(product, e)}
                    icon={faHeart}
                />
            </div>

            {/* image + name + price */}
            <Link to={`/Products/${productId}`} className="flex-1">
                <div className="w-5/6 h-[180px] flex justify-center items-center overflow-hidden mx-auto mb-3">
                    <img
                        src={product?.image_url}
                        alt={product?.name}
                        className="!h-auto !w-full object-contain"
                        loading="lazy"
                    />
                </div>
                <div>
                    <h3 className="text-sm font-extrabold text-center text-gray-800 line-clamp-2 min-h-[40px]">
                        {product?.name}
                    </h3>
                    <div className="flex items-center justify-center gap-3 my-2">
                        {hasDiscount && (
                            <h4 className="text-base text-gray-400 font-bold line-through">
                                EGP {originalPrice}
                            </h4>
                        )}
                        <h4 className="text-lg text-gray-600 font-bold">EGP {finalPrice}</h4>
                    </div>
                </div>
            </Link>

            {/* +/- quantity controls */}
            <div
                className="flex items-center justify-center gap-3 mb-3"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={decrease}
                    className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-mainColor text-mainColor font-bold text-lg leading-none hover:bg-mainColor hover:text-white transition-colors select-none"
                    disabled={qty <= 1}
                    style={{ opacity: qty <= 1 ? 0.4 : 1 }}
                >
                    −
                </button>
                <span className="w-8 text-center font-bold text-gray-800 text-base select-none">
                    {qty}
                </span>
                <button
                    onClick={increase}
                    className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-mainColor text-mainColor font-bold text-lg leading-none hover:bg-mainColor hover:text-white transition-colors select-none"
                    disabled={qty >= maxQty}
                    style={{ opacity: qty >= maxQty ? 0.4 : 1 }}
                >
                    +
                </button>
            </div>

            {/* add to cart button */}
            <div className="flex justify-center items-center">
                <button
                    onClick={(e) => onAddToCart && onAddToCart(product, qty, e)}
                    className="relative flex items-center justify-center w-full h-[40px] bg-mainColor rounded-lg border-none shadow-md cursor-pointer overflow-hidden transition duration-500 active:scale-95 group"
                >
                    <span className="absolute left-[-50px] flex items-center justify-center w-[30px] h-[30px] rounded-full bg-transparent overflow-hidden z-10 transition duration-500 group-hover:translate-x-[58px] group-hover:rounded-full">
                        <img src={CardSVG} alt="cart" />
                    </span>
                    <p className="flex items-center justify-center h-full text-sm font-semibold text-white z-10 transition duration-500 group-hover:translate-x-[10px]">
                        {language === "ar" ? "أضف إلى السلة" : "Add to Cart"}
                    </p>
                </button>
            </div>
        </div>
    );
});

ProductCard.displayName = "ProductCard";
export default ProductCard;
